import { CreateDPoPClientOpts, JWK } from '@sphereon/oid4vc-common'
import {
  AccessTokenRequestOpts,
  AccessTokenResponse,
  Alg,
  AuthorizationChallengeCodeResponse,
  AuthorizationChallengeErrorResponse,
  AuthorizationChallengeRequestOpts,
  AuthorizationRequestOpts,
  AuthorizationResponse,
  AuthorizationServerOpts,
  AuthzFlowType,
  CodeChallengeMethod,
  CredentialConfigurationSupported,
  CredentialConfigurationSupportedV1_0_15,
  CredentialOfferRequestWithBaseUrl,
  CredentialResponseV1_0_15,
  DefaultURISchemes,
  determineVersionsFromIssuerMetadata,
  DPoPResponseParams,
  EndpointMetadataResultV1_0_15,
  ExperimentalSubjectIssuance,
  getClientIdFromCredentialOfferPayload,
  getIssuerFromCredentialOfferPayload,
  getSupportedCredentials,
  getTypesFromCredentialSupported,
  KID_JWK_X5C_ERROR,
  NotificationRequest,
  NotificationResponseResult,
  OID4VCICredentialFormat,
  OpenId4VCIVersion,
  PKCEOpts,
  ProofOfPossessionCallbacks,
  toAuthorizationResponsePayload,
} from '@sphereon/oid4vci-common'
import { CredentialFormat, Loggers } from '@sphereon/ssi-types'

import { AccessTokenClient } from './AccessTokenClient'
import { acquireAuthorizationChallengeAuthCode, createAuthorizationRequestUrl } from './AuthorizationCodeClient'
import { CredentialOfferClient } from './CredentialOfferClient'
import { CredentialRequestOpts } from './CredentialRequestClient'
import { CredentialRequestClientBuilderV1_0_15 } from './CredentialRequestClientBuilderV1_0_15'
import { MetadataClient } from './MetadataClient'
import { ProofOfPossessionBuilder } from './ProofOfPossessionBuilder'
import { generateMissingPKCEOpts, sendNotification } from './functions'
import { OpenID4VCIClientStateV1_0_15, OpenID4VCIClientV1_0_15 } from './OpenID4VCIClientV1_0_15'

// FIXME This is kind of a "FrankenClient" rather than a proper delegate class. We got away with it before, but now fields are being removed or replaced or made array this is causing more and more technical depth

const logger = Loggers.DEFAULT.get('sphereon:oid4vci')

export type OpenID4VCIClientState = OpenID4VCIClientStateV1_0_15

export type EndpointMetadataResult = EndpointMetadataResultV1_0_15

export class OpenID4VCIClient {
  private readonly _state: OpenID4VCIClientState

  private constructor({
    credentialOffer,
    clientId,
    kid,
    alg,
    credentialIssuer,
    pkce,
    authorizationRequest,
    accessToken,
    jwk,
    endpointMetadata,
    accessTokenResponse,
    authorizationRequestOpts,
    authorizationCodeResponse,
    authorizationURL,
  }: {
    credentialOffer?: CredentialOfferRequestWithBaseUrl
    kid?: string
    alg?: Alg | string
    clientId?: string
    credentialIssuer?: string
    pkce?: PKCEOpts
    authorizationRequest?: AuthorizationRequestOpts // Can be provided here, or when manually calling createAuthorizationUrl
    jwk?: JWK
    accessToken?: string
    endpointMetadata?: EndpointMetadataResult
    accessTokenResponse?: AccessTokenResponse
    authorizationRequestOpts?: AuthorizationRequestOpts
    authorizationCodeResponse?: AuthorizationResponse | AuthorizationChallengeCodeResponse
    authorizationURL?: string
  }) {
    const issuer = credentialIssuer ?? (credentialOffer ? getIssuerFromCredentialOfferPayload(credentialOffer.credential_offer) : undefined)
    if (!issuer) {
      throw Error('No credential issuer supplied or deduced from offer')
    }
    this._state = {
      credentialOffer,
      credentialIssuer: issuer,
      kid,
      alg,
      // TODO: We need to refactor this and always explicitly call createAuthorizationRequestUrl, so we can have a credential selection first and use the kid as a default for the client id
      clientId: clientId ?? (credentialOffer && getClientIdFromCredentialOfferPayload(credentialOffer.credential_offer)) ?? kid?.split('#')[0],
      pkce: { disabled: false, codeChallengeMethod: CodeChallengeMethod.S256, ...pkce },
      authorizationRequestOpts,
      authorizationCodeResponse,
      accessToken,
      jwk,
      endpointMetadata: endpointMetadata as EndpointMetadataResultV1_0_15 | undefined,
      accessTokenResponse,
      authorizationURL,
    } as OpenID4VCIClientState
    // Running syncAuthorizationRequestOpts later as it is using the state
    if (!this._state.authorizationRequestOpts) {
      this._state.authorizationRequestOpts = this.syncAuthorizationRequestOpts(authorizationRequest)
    }
    logger.debug(`Authorization req options: ${JSON.stringify(this._state.authorizationRequestOpts, null, 2)}`)
  }

  public static async fromCredentialIssuer({
    kid,
    alg,
    retrieveServerMetadata,
    clientId,
    credentialIssuer,
    pkce,
    authorizationRequest,
    createAuthorizationRequestURL,
    endpointMetadata,
  }: {
    credentialIssuer: string
    kid?: string
    alg?: Alg | string
    retrieveServerMetadata?: boolean
    clientId?: string
    createAuthorizationRequestURL?: boolean
    authorizationRequest?: AuthorizationRequestOpts // Can be provided here, or when manually calling createAuthorizationUrl
    pkce?: PKCEOpts
    endpointMetadata?: EndpointMetadataResult
  }) {
    const client = new OpenID4VCIClient({
      kid,
      alg,
      clientId: clientId ?? authorizationRequest?.clientId,
      credentialIssuer,
      pkce,
      authorizationRequest,
      endpointMetadata,
    })
    if (retrieveServerMetadata === undefined || retrieveServerMetadata) {
      await client.retrieveServerMetadata()
    }
    if (createAuthorizationRequestURL === undefined || createAuthorizationRequestURL) {
      await client.createAuthorizationRequestUrl({ authorizationRequest, pkce })
    }
    return client
  }

  public static async fromState({ state }: { state: OpenID4VCIClientState | string }): Promise<OpenID4VCIClient> {
    const clientState = typeof state === 'string' ? JSON.parse(state) : state

    return new OpenID4VCIClient(clientState)
  }

  public static async fromURI({
    uri,
    kid,
    alg,
    retrieveServerMetadata,
    clientId,
    pkce,
    createAuthorizationRequestURL,
    authorizationRequest,
    resolveOfferUri,
    endpointMetadata,
  }: {
    uri: string
    kid?: string
    alg?: Alg | string
    retrieveServerMetadata?: boolean
    createAuthorizationRequestURL?: boolean
    resolveOfferUri?: boolean
    pkce?: PKCEOpts
    clientId?: string
    authorizationRequest?: AuthorizationRequestOpts // Can be provided here, or when manually calling createAuthorizationUrl
    endpointMetadata?: EndpointMetadataResult
  }): Promise<OpenID4VCIClient> {
    const credentialOfferClient = await CredentialOfferClient.fromURI(uri, { resolve: resolveOfferUri })
    const client = new OpenID4VCIClient({
      credentialOffer: credentialOfferClient,
      kid,
      alg,
      clientId: clientId ?? authorizationRequest?.clientId ?? credentialOfferClient.clientId,
      pkce,
      authorizationRequest,
      endpointMetadata,
    })

    if (retrieveServerMetadata === undefined || retrieveServerMetadata) {
      await client.retrieveServerMetadata()
    }
    if (
      credentialOfferClient.supportedFlows.includes(AuthzFlowType.AUTHORIZATION_CODE_FLOW) &&
      (createAuthorizationRequestURL === undefined || createAuthorizationRequestURL)
    ) {
      await client.createAuthorizationRequestUrl({ authorizationRequest, pkce })
      logger.debug(`Authorization Request URL: ${client._state.authorizationURL}`)
    }

    return client
  }

  /**
   * Allows you to create an Authorization Request URL when using an Authorization Code flow. This URL needs to be accessed using the front channel (browser)
   *
   * The Identity provider would present a login screen typically; after you authenticated, it would redirect to the provided redirectUri; which can be same device or cross-device
   * @param opts
   */
  public async createAuthorizationRequestUrl(opts?: {
    authorizationRequest?: AuthorizationRequestOpts
    pkce?: PKCEOpts
  }): Promise<string> {
    if (!this._state.authorizationURL) {
      this.calculatePKCEOpts(opts?.pkce)
      this._state.authorizationRequestOpts = this.syncAuthorizationRequestOpts(opts?.authorizationRequest)
      if (!this._state.authorizationRequestOpts) {
        throw Error(`No Authorization Request options present or provided in this call`)
      }

      if (
        this._state.endpointMetadata?.credentialIssuerMetadata &&
        'authorization_endpoint' in this._state.endpointMetadata.credentialIssuerMetadata
      ) {
        this._state.endpointMetadata.authorization_endpoint = this._state.endpointMetadata.credentialIssuerMetadata.authorization_endpoint as string
      }
      this._state.authorizationURL = await createAuthorizationRequestUrl({
        pkce: this._state.pkce,
        endpointMetadata: this.endpointMetadata as EndpointMetadataResultV1_0_15,
        authorizationRequest: this._state.authorizationRequestOpts,
        credentialOffer: this.credentialOffer,
        credentialConfigurationSupported: this.getCredentialsSupported() as Record<string, CredentialConfigurationSupportedV1_0_15>,
      })
    }
    return this._state.authorizationURL
  }

  public async retrieveServerMetadata(): Promise<EndpointMetadataResult> {
    this.assertIssuerData()
    if (!this._state.endpointMetadata) {
      if (this.credentialOffer) {
        this._state.endpointMetadata = await MetadataClient.retrieveAllMetadataFromCredentialOffer(this.credentialOffer)
      } else if (this._state.credentialIssuer) {
        this._state.endpointMetadata = await MetadataClient.retrieveAllMetadata(this._state.credentialIssuer)
      } else {
        throw Error(`Cannot retrieve issuer metadata without either a credential offer, or issuer value`)
      }
    }

    return this.endpointMetadata
  }

  private calculatePKCEOpts(pkce?: PKCEOpts) {
    this._state.pkce = generateMissingPKCEOpts({ ...this._state.pkce, ...pkce })
  }

  public async acquireAuthorizationChallengeCode(opts?: AuthorizationChallengeRequestOpts): Promise<AuthorizationChallengeCodeResponse> {
    const response = await acquireAuthorizationChallengeAuthCode({
      metadata: this.endpointMetadata,
      credentialIssuer: this.getIssuer(),
      clientId: this._state.clientId ?? this._state.authorizationRequestOpts?.clientId,
      ...opts,
    })

    if (response.errorBody) {
      logger.debug(`Authorization code error:\r\n${JSON.stringify(response.errorBody)}`)
      const error = response.errorBody as AuthorizationChallengeErrorResponse
      return Promise.reject(error)
    } else if (!response.successBody) {
      logger.debug(`Authorization code error. No success body`)
      return Promise.reject(
        Error(
          `Retrieving an authorization code token from ${this._state.endpointMetadata?.authorization_challenge_endpoint} for issuer ${this.getIssuer()} failed as there was no success response body`,
        ),
      )
    }

    return { ...response.successBody }
  }

  public async acquireAccessToken(
    opts?: Omit<AccessTokenRequestOpts, 'credentialOffer' | 'credentialIssuer' | 'metadata' | 'additionalParams'> & {
      clientId?: string
      authorizationResponse?: string | AuthorizationResponse | AuthorizationChallengeCodeResponse // Pass in an auth response, either as URI/redirect, or object
      additionalRequestParams?: Record<string, any>
    },
  ): Promise<AccessTokenResponse & { params?: DPoPResponseParams }> {
    const { pin, clientId = this._state.clientId ?? this._state.authorizationRequestOpts?.clientId } = opts ?? {}
    let { redirectUri } = opts ?? {}

    const code = this.getAuthorizationCode(opts?.authorizationResponse, opts?.code)

    if (opts?.codeVerifier) {
      this._state.pkce.codeVerifier = opts.codeVerifier
    }
    this.assertIssuerData()

    const asOpts: AuthorizationServerOpts = { ...opts?.asOpts }
    const kid = asOpts.clientOpts?.kid ?? this._state.kid ?? this._state.authorizationRequestOpts?.requestObjectOpts?.kid
    const clientAssertionType =
      asOpts.clientOpts?.clientAssertionType ??
      (kid && clientId && typeof asOpts.clientOpts?.signCallbacks?.signCallback === 'function'
        ? 'urn:ietf:params:oauth:client-assertion-type:jwt-bearer'
        : undefined)
    if (this.isEBSI() || (clientId && kid)) {
      if (!clientId) {
        throw Error(`Client id expected for EBSI`)
      }
      asOpts.clientOpts = {
        ...asOpts.clientOpts,
        clientId,
        ...(kid && { kid }),
        ...(clientAssertionType && { clientAssertionType }),
        signCallbacks: asOpts.clientOpts?.signCallbacks ?? this._state.authorizationRequestOpts?.requestObjectOpts?.signCallbacks,
      }
    }

    if (clientId) {
      this._state.clientId = clientId
      if (!asOpts.clientOpts) {
        asOpts.clientOpts = { clientId }
      }
      asOpts.clientOpts.clientId = clientId
    }
    if (!this._state.accessTokenResponse) {
      const accessTokenClient = new AccessTokenClient()

      if (redirectUri && redirectUri !== this._state.authorizationRequestOpts?.redirectUri) {
        console.log(
          `Redirect URI mismatch between access-token (${redirectUri}) and authorization request (${this._state.authorizationRequestOpts?.redirectUri}). According to the specification that is not allowed.`,
        )
      }
      if (this._state.authorizationRequestOpts?.redirectUri && !redirectUri) {
        redirectUri = this._state.authorizationRequestOpts.redirectUri
      }

      const response = await accessTokenClient.acquireAccessToken({
        credentialOffer: this.credentialOffer,
        metadata: this.endpointMetadata,
        credentialIssuer: this.getIssuer(),
        pin,
        ...(!this._state.pkce.disabled && { codeVerifier: this._state.pkce.codeVerifier }),
        code,
        redirectUri,
        asOpts,
        ...(opts?.createDPoPOpts && { createDPoPOpts: opts.createDPoPOpts }),
        ...(opts?.additionalRequestParams && { additionalParams: opts.additionalRequestParams }),
      })

      if (response.errorBody) {
        const errorDetail = typeof response.errorBody === 'object' ? JSON.stringify(response.errorBody) : String(response.errorBody)
        logger.error(`Access token error response (status ${response.origResponse.status}):\r\n${errorDetail}`)
        throw Error(
          `Retrieving an access token from ${this._state.endpointMetadata?.token_endpoint} for issuer ${this.getIssuer()} failed with status: ${
            response.origResponse.status
          }. Response: ${errorDetail}`,
        )
      } else if (!response.successBody) {
        logger.debug(`Access token error. No success body`)
        throw Error(
          `Retrieving an access token from ${
            this._state.endpointMetadata?.token_endpoint
          } for issuer ${this.getIssuer()} failed as there was no success response body`,
        )
      }
      this._state.accessTokenResponse = response.successBody
      this._state.dpopResponseParams = response.params
      this._state.accessToken = response.successBody.access_token

      if (response.successBody.c_nonce) {
        this._state.cachedCNonce = response.successBody.c_nonce
      }
    }

    return { ...this.accessTokenResponse, ...(this.dpopResponseParams && { params: this.dpopResponseParams }) }
  }

  public async acquireCredentials({
    credentialIdentifier,
    credentialConfigurationId,
    credentialTypes,
    context,
    proofCallbacks,
    format,
    kid,
    jwk,
    alg,
    jti,
    deferredCredentialAwait,
    deferredCredentialIntervalInMS,
    createDPoPOpts,
  }: {
    credentialIdentifier?: string
    credentialConfigurationId?: string
    credentialTypes?: string | string[]
    context?: string[]
    proofCallbacks: ProofOfPossessionCallbacks
    format: CredentialFormat | OID4VCICredentialFormat
    kid?: string
    jwk?: JWK
    alg?: Alg | string
    jti?: string
    deferredCredentialAwait?: boolean
    deferredCredentialIntervalInMS?: number
    experimentalHolderIssuanceSupported?: boolean
    createDPoPOpts?: CreateDPoPClientOpts
  }): Promise<CredentialResponseV1_0_15 & { params?: DPoPResponseParams; access_token: string }> {
    if ([jwk, kid].filter((v) => v !== undefined).length > 1) {
      throw new Error(KID_JWK_X5C_ERROR + `. jwk: ${jwk !== undefined}, kid: ${kid !== undefined}`)
    }

    if (alg) this._state.alg = alg
    if (jwk) this._state.jwk = jwk
    if (kid) this._state.kid = kid

    // Acquire nonce if we don't have one cached. Both d15 (nonce endpoint only) and
    // V1.0 (nonce from token response OR nonce endpoint) are supported.
    if (!this._state.cachedCNonce && this.hasNonceEndpoint()) {
      try {
        await this.acquireNonceViaV15Delegate()
      } catch (e) {
        return Promise.reject(Error(`failed to acquire nonce: ${String(e)}`))
      }
    }

    let requestBuilder: CredentialRequestClientBuilderV1_0_15 = this.credentialOffer
      ? CredentialRequestClientBuilderV1_0_15.fromCredentialOffer({
          credentialOffer: this.credentialOffer,
          metadata: this.endpointMetadata as EndpointMetadataResultV1_0_15,
        })
      : CredentialRequestClientBuilderV1_0_15.fromCredentialIssuer({
          credentialIssuer: this.getIssuer(),
          credentialTypes,
          credentialIdentifier,
          credentialConfigurationId,
          metadata: this.endpointMetadata as EndpointMetadataResultV1_0_15,
          version: this.version(),
        })

    if (credentialIdentifier) {
      requestBuilder.withCredentialIdentifier(credentialIdentifier)
    } else if (credentialConfigurationId) {
      requestBuilder.withCredentialConfigurationId(credentialConfigurationId)
    }

    // If we are in an auth code flow, without a c nonce, we return the issuerState back to the issuer in case it is present
    const issuerState =
      this.issuerSupportedFlowTypes().includes(AuthzFlowType.AUTHORIZATION_CODE_FLOW) &&
      this._state.authorizationCodeResponse &&
      !this.accessTokenResponse?.c_nonce &&
      this._state.credentialOffer?.issuerState
        ? this._state.credentialOffer.issuerState
        : undefined
    requestBuilder.withIssuerState(issuerState)

    requestBuilder.withTokenFromResponse(this.accessTokenResponse)
    requestBuilder.withDeferredCredentialAwait(deferredCredentialAwait ?? false, deferredCredentialIntervalInMS)
    let subjectIssuance: ExperimentalSubjectIssuance | undefined
    if (this.endpointMetadata?.credentialIssuerMetadata && credentialTypes) {
      const metadata = this.endpointMetadata.credentialIssuerMetadata
      const types = Array.isArray(credentialTypes) ? credentialTypes : [credentialTypes]

      if (metadata.credentials_supported && Array.isArray(metadata.credentials_supported)) {
        let typeSupported = false

        metadata.credentials_supported.forEach((supportedCredential) => {
          const subTypes = getTypesFromCredentialSupported(supportedCredential)
          if (
            subTypes.every((t, i) => types[i] === t) ||
            (types.length === 1 && (types[0] === supportedCredential.id || subTypes.includes(types[0])))
          ) {
            typeSupported = true
            if (supportedCredential.credential_subject_issuance) {
              subjectIssuance = { credential_subject_issuance: supportedCredential.credential_subject_issuance }
            }
          }
        })

        if (!typeSupported) {
          console.log(`Not all credential types ${JSON.stringify(credentialTypes)} are present in metadata for ${this.getIssuer()}`)
          // throw Error(`Not all credential types ${JSON.stringify(credentialTypes)} are supported by issuer ${this.getIssuer()}`);
        }
      } else if (metadata.credentials_supported && !Array.isArray(metadata.credentials_supported)) {
        const credentialsSupported = metadata.credentials_supported
        if (types.some((type) => !metadata.credentials_supported || !credentialsSupported[type])) {
          throw Error(`Not all credential types ${JSON.stringify(credentialTypes)} are supported by issuer ${this.getIssuer()}`)
        }
      }
      // todo: Format check? We might end up with some disjoint type / format combinations supported by the server
    }
    if (subjectIssuance) {
      requestBuilder.withSubjectIssuance(subjectIssuance)
    }

    const credentialRequestClient = requestBuilder.build()

    try {
      const proofBuilder = ProofOfPossessionBuilder.fromAccessTokenResponse({
        accessTokenResponse: this.accessTokenResponse,
        callbacks: proofCallbacks,
        version: this.version(),
      })
        .withIssuer(this.getIssuer())
        .withAlg(this.alg)

      if (this._state.jwk) {
        proofBuilder.withJWK(this._state.jwk)
      }
      if (this._state.kid) {
        proofBuilder.withKid(this._state.kid)
      }

      if (
        this.clientId &&
        (!this.credentialOffer ||
          (this.credentialOffer.supportedFlows.includes(AuthzFlowType.AUTHORIZATION_CODE_FLOW) && !this.credentialOffer.preAuthorizedCode))
      ) {
        proofBuilder.withClientId(this.clientId)
      }
      if (jti) {
        proofBuilder.withJti(jti)
      }
      if ('cachedCNonce' in this._state && this._state.cachedCNonce) {
        proofBuilder.withAccessTokenNonce(this._state.cachedCNonce)
      }

      const response = await credentialRequestClient.acquireCredentialsUsingProof({
        proofInput: proofBuilder,
        credentialTypes: credentialTypes ?? credentialIdentifier ?? credentialConfigurationId,
        context,
        format,
        subjectIssuance,
        createDPoPOpts,
      })

      this._state.dpopResponseParams = response.params
      if (response.errorBody) {
        logger.debug(`Credential request error:\r\n${JSON.stringify(response.errorBody)}`)
        const errDesc = response.errorBody.error_description
          ? `: ${response.errorBody.error_description}`
          : response.errorBody.error
            ? `: ${response.errorBody.error}`
            : ''
        throw Error(
          `Retrieving a credential from ${this._state.endpointMetadata?.credential_endpoint} for issuer ${this.getIssuer()} failed with status: ${response.origResponse.status}${errDesc}`,
        )
      } else if (!response.successBody) {
        logger.debug(`Credential request error. No success body`)
        throw Error(
          `Retrieving a credential from ${this._state.endpointMetadata?.credential_endpoint} for issuer ${this.getIssuer()} failed as there was no success response body`,
        )
      }
      return {
        ...response.successBody,
        ...(this.dpopResponseParams && { params: this.dpopResponseParams }),
        access_token: response.access_token,
      }
    } catch (e) {
      if (!this.shouldRetryWithFreshNonce(e)) {
        return Promise.reject(e instanceof Error ? e : Error(String(e)))
      }
      // one retry with fresh nonce + rebuilt proof
      ;(this._state as OpenID4VCIClientStateV1_0_15).cachedCNonce = undefined

      try {
        await this.acquireNonceViaV15Delegate()
      } catch (e2) {
        return Promise.reject(Error(`retry nonce fetch failed: ${String(e2)}`))
      }

      // rebuild proof using the fresh nonce and resend
      const proofBuilder2 = ProofOfPossessionBuilder.fromAccessTokenResponse({
        accessTokenResponse: this.accessTokenResponse,
        callbacks: proofCallbacks,
        version: this.version(),
      })
        .withIssuer(this.getIssuer())
        .withAlg(this.alg)

      if (this._state.jwk) {
        proofBuilder2.withJWK(this._state.jwk)
      }
      if (this._state.kid) {
        proofBuilder2.withKid(this._state.kid)
      }

      if (this.clientId) {
        proofBuilder2.withClientId(this.clientId)
      }
      if (jti) {
        proofBuilder2.withJti(jti)
      }

      const response2 = await credentialRequestClient.acquireCredentialsUsingProof({
        proofInput: proofBuilder2,
        credentialTypes,
        context,
        format,
        subjectIssuance,
        createDPoPOpts,
      })

      this._state.dpopResponseParams = response2.params
      if (response2.errorBody) {
        logger.debug(`Credential request error (after retry):\r\n${JSON.stringify(response2.errorBody)}`)
        const errDesc2 = response2.errorBody.error_description
          ? `: ${response2.errorBody.error_description}`
          : response2.errorBody.error
            ? `: ${response2.errorBody.error}`
            : ''
        return Promise.reject(
          Error(
            `Retrieving a credential from ${this._state.endpointMetadata?.credential_endpoint} for issuer ${this.getIssuer()} failed after retry with status: ${response2.origResponse.status}${errDesc2}`,
          ),
        )
      } else if (!response2.successBody) {
        logger.debug(`Credential request error after retry. No success body`)
        return Promise.reject(
          Error(
            `Retrieving a credential from ${this._state.endpointMetadata?.credential_endpoint} for issuer ${this.getIssuer()} failed after retry as there was no success response body`,
          ),
        )
      }
      return {
        ...response2.successBody,
        ...(this.dpopResponseParams && { params: this.dpopResponseParams }),
        access_token: response2.access_token,
      }
    }
  }

  public async exportState(): Promise<string> {
    return JSON.stringify(this._state)
  }

  getCredentialsSupported(
    format?: (OID4VCICredentialFormat | string) | (OID4VCICredentialFormat | string)[],
  ): Record<string, CredentialConfigurationSupportedV1_0_15> | Array<CredentialConfigurationSupported> {
    return getSupportedCredentials({
      issuerMetadata: this.endpointMetadata.credentialIssuerMetadata,
      version: this.version(),
      format: format,
    })
  }

  public async sendNotification(
    credentialRequestOpts: Partial<CredentialRequestOpts>,
    request: NotificationRequest,
    accessToken?: string,
  ): Promise<NotificationResponseResult> {
    return sendNotification(credentialRequestOpts, request, accessToken ?? this._state.accessToken ?? this._state.accessTokenResponse?.access_token)
  }

  issuerSupportedFlowTypes(): AuthzFlowType[] {
    return (
      this.credentialOffer?.supportedFlows ??
      ((this._state.endpointMetadata?.credentialIssuerMetadata?.authorization_endpoint ?? this._state.endpointMetadata?.authorization_server)
        ? [AuthzFlowType.AUTHORIZATION_CODE_FLOW]
        : [])
    )
  }

  isFlowTypeSupported(flowType: AuthzFlowType): boolean {
    return this.issuerSupportedFlowTypes().includes(flowType)
  }

  get authorizationURL(): string | undefined {
    return this._state.authorizationURL
  }

  public hasAuthorizationURL(): boolean {
    return !!this.authorizationURL
  }

  get credentialOffer(): CredentialOfferRequestWithBaseUrl | undefined {
    return this._state.credentialOffer
  }

  public version(): OpenId4VCIVersion {
    // Metadata-based detection takes precedence since it has discriminating fields.
    // The offer format is identical for d15 and V1.0, so offer-based detection cannot distinguish them.
    const metadata = this._state.endpointMetadata
    if (metadata?.credentialIssuerMetadata) {
      const versions = determineVersionsFromIssuerMetadata(metadata.credentialIssuerMetadata)
      if (versions.length > 0 && !versions.includes(OpenId4VCIVersion.VER_UNKNOWN)) {
        return versions[0]
      }
    }
    if (this.credentialOffer?.version && this.credentialOffer.version !== OpenId4VCIVersion.VER_UNKNOWN) {
      return this.credentialOffer.version
    }
    return OpenId4VCIVersion.VER_1_0
  }

  public get endpointMetadata(): EndpointMetadataResult {
    this.assertServerMetadata()
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._state.endpointMetadata!
  }

  get kid(): string {
    this.assertIssuerData()
    if (!this._state.kid) {
      throw new Error('No value for kid is supplied')
    }
    return this._state.kid
  }

  get alg(): string {
    this.assertIssuerData()
    if (!this._state.alg) {
      throw new Error('No value for alg is supplied')
    }
    return this._state.alg
  }

  set clientId(value: string | undefined) {
    this._state.clientId = value
  }

  get clientId(): string | undefined {
    return this._state.clientId
  }

  public hasAccessTokenResponse(): boolean {
    return !!this._state.accessTokenResponse
  }

  get accessTokenResponse(): AccessTokenResponse {
    this.assertAccessToken()
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._state.accessTokenResponse!
  }

  get dpopResponseParams(): DPoPResponseParams | undefined {
    return this._state.dpopResponseParams
  }

  public getIssuer(): string {
    this.assertIssuerData()
    return this._state.credentialIssuer
  }

  public getAccessTokenEndpoint(): string {
    this.assertIssuerData()
    if (this.endpointMetadata) {
      return this.endpointMetadata.token_endpoint
    }
    return AccessTokenClient.determineTokenURL({ issuerOpts: { issuer: this.getIssuer() } })
  }

  public getCredentialEndpoint(): string {
    this.assertIssuerData()
    return this.endpointMetadata ? this.endpointMetadata.credential_endpoint : `${this.getIssuer()}/credential`
  }

  public getAuthorizationChallengeEndpoint(): string | undefined {
    this.assertIssuerData()
    return this.endpointMetadata?.authorization_challenge_endpoint
  }

  public hasAuthorizationChallengeEndpoint(): boolean {
    return !!this.getAuthorizationChallengeEndpoint()
  }

  public hasDeferredCredentialEndpoint(): boolean {
    return !!this.getAccessTokenEndpoint()
  }

  public getDeferredCredentialEndpoint(): string {
    this.assertIssuerData()
    return this.endpointMetadata ? this.endpointMetadata.credential_endpoint : `${this.getIssuer()}/credential`
  }

  /**
   * Too bad we need a method like this, but EBSI is not exposing metadata
   */
  public isEBSI() {
    // FIXME SSISDK-40 CredentialOfferPayloadV1_0_15 no longer has credentials
    // if (
    //   this.credentialOffer &&
    //   (this.credentialOffer?.credential_offer as CredentialOfferPayloadV1_0_15)?.credentials?.find(
    //     (cred) =>
    //       // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //       // @ts-ignore
    //       typeof cred !== 'string' && 'trust_framework' in cred && 'name' in cred.trust_framework && cred.trust_framework.name.includes('ebsi')
    //   )
    // ) {
    //   return true
    // }
    // this.assertIssuerData();
    return (
      this.clientId?.includes('ebsi') ||
      this._state.kid?.includes('did:ebsi:') ||
      this.getIssuer().includes('ebsi') ||
      this.endpointMetadata.credentialIssuerMetadata?.authorization_endpoint?.includes('ebsi.eu') ||
      this.endpointMetadata.credentialIssuerMetadata?.authorization_server?.includes('ebsi.eu')
    )
  }

  private assertIssuerData(): void {
    if (!this._state.credentialIssuer) {
      throw Error(`No credential issuer value present`)
    } else if (!this._state.credentialOffer && this._state.endpointMetadata && this.issuerSupportedFlowTypes().length === 0) {
      throw Error(`No issuance initiation or credential offer present`)
    }
  }

  private assertServerMetadata(): void {
    if (!this._state.endpointMetadata) {
      throw Error('No server metadata')
    }
  }

  private assertAccessToken(): void {
    if (!this._state.accessTokenResponse) {
      throw Error(`No access token present`)
    }
  }

  private syncAuthorizationRequestOpts(opts?: AuthorizationRequestOpts): AuthorizationRequestOpts {
    const requestObjectOpts = { ...this._state?.authorizationRequestOpts?.requestObjectOpts, ...opts?.requestObjectOpts }
    let authorizationRequestOpts = {
      ...this._state?.authorizationRequestOpts,
      ...opts,
      ...(requestObjectOpts && { requestObjectOpts }),
    } as AuthorizationRequestOpts
    if (!authorizationRequestOpts) {
      // We only set a redirectUri if no options are provided.
      // Note that this only works for mobile apps, that can handle a code query param on the default openid-credential-offer deeplink.
      // Provide your own options if that is not desired!
      authorizationRequestOpts = { redirectUri: `${DefaultURISchemes.CREDENTIAL_OFFER}://` }
    }
    const clientId = authorizationRequestOpts.clientId ?? this._state.clientId
    // sync clientId
    this._state.clientId = clientId
    authorizationRequestOpts.clientId = clientId
    return authorizationRequestOpts
  }

  private getAuthorizationCode = (
    authorizationResponse?: string | AuthorizationResponse | AuthorizationChallengeCodeResponse,
    code?: string,
  ): string | undefined => {
    if (authorizationResponse) {
      this._state.authorizationCodeResponse = { ...toAuthorizationResponsePayload(authorizationResponse) }
    } else if (code) {
      this._state.authorizationCodeResponse = { code }
    }

    return (
      (this._state.authorizationCodeResponse as AuthorizationResponse)?.code ??
      (this._state.authorizationCodeResponse as AuthorizationChallengeCodeResponse)?.authorization_code
    )
  }

  private hasNonceEndpoint(): boolean {
    const endpoint = this._state.endpointMetadata?.credentialIssuerMetadata?.nonce_endpoint
    return typeof endpoint === 'string' && endpoint.length > 0
  }

  private async acquireNonceViaV15Delegate(): Promise<void> {
    const state: OpenID4VCIClientStateV1_0_15 = this._state as OpenID4VCIClientStateV1_0_15
    let v15Client
    try {
      v15Client = await OpenID4VCIClientV1_0_15.fromState({ state })
    } catch (e) {
      return Promise.reject(Error(`failed to init v15 delegate for nonce: ${String(e)}`))
    }
    try {
      await v15Client.acquireNonce()
    } catch (e) {
      return Promise.reject(Error(`nonce request failed: ${String(e)}`))
    }
    state.cachedCNonce = v15Client.state.cachedCNonce
  }

  private shouldRetryWithFreshNonce(err: unknown): boolean {
    // V1.0 can get c_nonce from error responses; d15 requires a nonce endpoint.
    // Both versions >= d15 support nonce retry when a nonce endpoint exists.
    const canRetry = this.hasNonceEndpoint() || this.version() >= OpenId4VCIVersion.VER_1_0
    if (!canRetry) {
      return false
    }

    const status = (err as any)?.response?.status ?? (err as any)?.status

    const body = (err as any)?.response?.data ?? (err as any)?.data ?? undefined

    const error = typeof body?.error === 'string' ? body.error : undefined
    const desc = typeof body?.error_description === 'string' ? body.error_description : undefined
    const text = [error, desc].filter(Boolean).join(' ').toLowerCase()

    // Strong hints the nonce/proof is the issue
    if (status === 400 || status === 401 || status === 403) {
      // Common signals to treat as nonce/proof freshness problems
      if (text.includes('nonce') || text.includes('c_nonce')) {
        return true
      }
      if (text.includes('proof') && (text.includes('invalid') || text.includes('expired'))) {
        return true
      }
      // Some issuers use generic messages but still accept a fresh nonce on retry
      if (error === 'invalid_proof' || error === 'invalid_request') {
        return true
      }
    }

    return false
  }
}
